import React from "react"
import { Button, Grid, makeStyles, TextField } from "@material-ui/core"
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline"
import { v4 as uuidv4 } from "uuid"
import { fieldPropTypes } from "redux-form"
import { defineMessages } from "react-intl"
import AddIcon from "@material-ui/icons/Add"
import PropTypes from "prop-types"
import TooltipIconButton from "../buttons/TooltipIconButton"
import { words } from "../../i18n/lstrings"
import useSmartFormat from "../../i18n/useSmartFormat"
import { WIDGET_TYPES } from "../../widgets/types"

const useStyles = makeStyles(() => ({
  shortTextField: {
    minWidth: 240
  },
  longTextField: {
    minWidth: 400
  }
}))

const messages = defineMessages({
  dashboard: {
    id: "widget.form.dashboard",
    description: "widget dashboard",
    defaultMessage: "Dashboard"
  },
  bookmark: {
    id: "widget.form.bookmark",
    description: "widget bookmark",
    defaultMessage: "Bookmark"
  },
  addVisitorProfile: {
    id: "widget.form.dashboard.addVisitorProfile",
    defaultMessage: "Add a visitor profile"
  },
  visitorProfileCode: {
    id: "widget.form.dashboard.visitorProfileCode",
    defaultMessage: "Visitor profile code"
  }
})

/**
 * Constructs a widget by visitor profile row given a widget type and default values
 * @param {string} widgetType - Type of the widget
 * @param {string} id
 * @param {string} visitorProfileCode
 * @param {string} dashboardUuid
 * @param {string} bookmarkName
 * @param {string} selector
 * @returns {{id: *, visitorProfileCode: string, dashboardUuid: string}|{selector: string, id: *, visitorProfileCode: string}|{bookmarkName: string, id: *, visitorProfileCode: string, dashboardUuid: string}}
 */
export function makeWidgetsByVisitorProfileRow(
  widgetType,
  {
    id = uuidv4(),
    visitorProfileCode = "",
    dashboardUuid = "",
    bookmarkName = "",
    selector = ""
  } = {}
) {
  switch (widgetType) {
    case WIDGET_TYPES.DASHBOARD:
      return {
        id,
        visitorProfileCode,
        dashboardUuid
      }
    case WIDGET_TYPES.BOOKMARK:
      return {
        id,
        visitorProfileCode,
        dashboardUuid,
        bookmarkName
      }
    case WIDGET_TYPES.SELECTOR:
      return {
        id,
        visitorProfileCode,
        selector
      }
    default:
      throw new Error(
        `Attempted to construct row for unknown widget type ${widgetType}`
      )
  }
}

function WidgetsByVisitorProfileRow({
  widget,
  widgetType,
  onRemove,
  canRemove,
  onChange
}) {
  const classes = useStyles()
  const format = useSmartFormat()
  const isDashboardOrBookmark =
    widgetType === WIDGET_TYPES.DASHBOARD ||
    widgetType === WIDGET_TYPES.BOOKMARK

  function onVisitorProfileChange(event) {
    onChange({
      ...widget,
      visitorProfileCode: event.target.value
    })
  }

  function onDashboardUuidChange(event) {
    onChange({
      ...widget,
      dashboardUuid: event.target.value
    })
  }

  function onBookmarkNameChange(event) {
    onChange({
      ...widget,
      bookmarkName: event.target.value
    })
  }

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      spacing={1}
      wrap="nowrap"
    >
      <Grid item>
        <TextField
          className={classes.shortTextField}
          fullWidth
          size="small"
          placeholder={format(messages.visitorProfileCode)}
          value={widget.visitorProfileCode}
          onChange={onVisitorProfileChange}
          variant="outlined"
        />
      </Grid>

      {isDashboardOrBookmark && (
        <Grid item>
          <TextField
            className={classes.longTextField}
            fullWidth
            size="small"
            placeholder={format(messages.dashboard)}
            value={widget.dashboardUuid}
            onChange={onDashboardUuidChange}
            variant="outlined"
          />
        </Grid>
      )}

      {widgetType === WIDGET_TYPES.BOOKMARK && (
        <Grid item>
          <TextField
            className={classes.longTextField}
            fullWidth
            size="small"
            placeholder={format(messages.bookmark)}
            value={widget.bookmarkName}
            onChange={onBookmarkNameChange}
            variant="outlined"
          />
        </Grid>
      )}

      {widgetType === WIDGET_TYPES.SELECTOR && (
        <Grid item>
          <TextField
            className={classes.longTextField}
            fullWidth
            size="small"
            placeholder={format(messages.dashboard)}
            variant="outlined"
          />
        </Grid>
      )}
      <Grid item>
        <TooltipIconButton
          onClick={onRemove}
          label={words.delete}
          disabled={!canRemove}
        >
          <RemoveCircleOutlineIcon />
        </TooltipIconButton>
      </Grid>
    </Grid>
  )
}

WidgetsByVisitorProfileRow.propTypes = {
  widget: PropTypes.object.isRequired,
  widgetType: PropTypes.oneOf(Object.values(WIDGET_TYPES)).isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired
}

export default function FieldWidgetsByVisitorProfile({
  input: { value, onChange },
  meta,
  widgetType,
  ...rest
}) {
  const format = useSmartFormat()
  const widgets = Array.isArray(value) ? value : []

  function onAddVisitorProfileClick() {
    onChange([...widgets, makeWidgetsByVisitorProfileRow(widgetType)])
  }

  function onRemoveWidget(widgetToRemove) {
    onChange(widgets.filter((widget) => widget !== widgetToRemove))
  }

  function onChangeWidget(id, newWidget) {
    onChange(widgets.map((widget) => (widget.id === id ? newWidget : widget)))
  }

  return (
    <Grid container direction="column" wrap="nowrap" {...rest}>
      {widgets.map((widget) => (
        <Grid item key={widget.id}>
          <WidgetsByVisitorProfileRow
            widget={widget}
            widgetType={widgetType}
            onRemove={() => onRemoveWidget(widget)}
            onChange={(newWidget) => onChangeWidget(widget.id, newWidget)}
            canRemove={widgets.length > 1}
          />
        </Grid>
      ))}

      <Grid item>
        <Button
          startIcon={<AddIcon />}
          size="small"
          onClick={onAddVisitorProfileClick}
        >
          {format(messages.addVisitorProfile)}
        </Button>
      </Grid>
    </Grid>
  )
}

FieldWidgetsByVisitorProfile.propTypes = {
  ...fieldPropTypes
}
